import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import PortalLayout from '../../../components/PortalLayout';
import axios from 'axios';

const FAQManagement = () => {
  const { token } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [formData, setFormData] = useState({ question: '', answer: '', category: 'General', order: 0, is_published: true });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/faqs/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqs(response.data.faqs);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFAQ) {
        await axios.put(`${BACKEND_URL}/api/faqs/admin/${editingFAQ.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${BACKEND_URL}/api/faqs/admin`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditingFAQ(null);
      setFormData({ question: '', answer: '', category: 'General', order: 0, is_published: true });
      fetchFAQs();
    } catch (error) {
      alert('Error saving FAQ');
    }
  };

  const deleteFAQ = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/faqs/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchFAQs();
    } catch (error) {
      alert('Error deleting FAQ');
    }
  };

  const editFAQ = (faq) => {
    setEditingFAQ(faq);
    setFormData({ question: faq.question, answer: faq.answer, category: faq.category, order: faq.order, is_published: faq.is_published });
    setShowModal(true);
  };

  const categories = [...new Set(faqs.map(f => f.category))];
  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <PortalLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">FAQ Management</h1>
            <p className="text-gray-600">Manage frequently asked questions</p>
          </div>
          <Button onClick={() => { setEditingFAQ(null); setFormData({ question: '', answer: '', category: 'General', order: 0, is_published: true }); setShowModal(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-5 h-5 mr-2" />New FAQ
          </Button>
        </div>

        <div className="bg-white rounded-xl p-4 mb-6 border border-gray-200">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search FAQs..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg" />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-4">
          {filteredFAQs.map((faq) => (
            <div key={faq.id} className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">{faq.category}</span>
                    <span className={`px-2 py-1 rounded text-xs ${faq.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{faq.is_published ? 'Published' : 'Draft'}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{faq.answer}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button onClick={() => editFAQ(faq)} size="sm" variant="outline"><Edit className="w-4 h-4" /></Button>
                  <Button onClick={() => deleteFAQ(faq.id)} size="sm" variant="outline" className="border-red-300 text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl p-8 max-w-2xl w-full">
              <h2 className="text-2xl font-bold mb-6">{editingFAQ ? 'Edit FAQ' : 'Create FAQ'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><label className="block text-sm font-semibold mb-2">Question</label><input type="text" required value={formData.question} onChange={(e) => setFormData({...formData, question: e.target.value})} className="w-full px-4 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-semibold mb-2">Answer</label><textarea required value={formData.answer} onChange={(e) => setFormData({...formData, answer: e.target.value})} rows="4" className="w-full px-4 py-2 border rounded-lg" /></div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-semibold mb-2">Category</label><input type="text" required value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-4 py-2 border rounded-lg" /></div>
                  <div><label className="block text-sm font-semibold mb-2">Order</label><input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: Number(e.target.value)})} className="w-full px-4 py-2 border rounded-lg" /></div>
                </div>
                <div><label className="flex items-center"><input type="checkbox" checked={formData.is_published} onChange={(e) => setFormData({...formData, is_published: e.target.checked})} className="mr-2" />Published</label></div>
                <div className="flex gap-3">
                  <Button type="submit" className="flex-1 bg-blue-600">Save</Button>
                  <Button type="button" onClick={() => setShowModal(false)} variant="outline" className="flex-1">Cancel</Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default FAQManagement;
